import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { applyTheme as applySharedTheme, type ThemeOption } from '@workspace/ui';
import type { ThemeEffect } from '@workspace/design-tokens';
import { DEFAULT_THEME_EFFECT, isThemeEffect } from '@workspace/design-tokens';

export type LayoutOption = 'side-menu' | 'top-menu';
export type ToastPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export type MapBasemapTheme = Exclude<ThemeOption, 'auto'>;

export interface NavigationPreferences {
  favorites: string[];
  recents: string[];
  collapsed: boolean;
  favoritesOrder: 'alpha' | 'manual';
}

const DEFAULT_THEME: ThemeOption = 'auto';
const DEFAULT_LAYOUT: LayoutOption = 'side-menu';
const DEFAULT_TOAST_POSITION: ToastPosition = 'bottom-right';
const DEFAULT_BASEMAP_THEME: MapBasemapTheme = 'light';
const DEFAULT_NAVIGATION_PREFERENCES: NavigationPreferences = {
  favorites: [],
  recents: [],
  collapsed: false,
  favoritesOrder: 'alpha'
};

const THEME_STORAGE_KEY = 'theme';
const EFFECT_STORAGE_KEY = 'theme_effect';
const THEME_LOG_PREFIX = '[PortalTheme]';
const THEME_PREF_KEY = 'ui.theme';
const THEME_EFFECT_KEY = 'ui.theme.effect';
const LAYOUT_PREF_KEY = 'ui.layout';
const TOAST_POSITION_KEY = 'ui.theme.toast_position';
const BASEMAP_THEME_KEY = 'maps.basemap_theme';
const BASEMAP_THEME_OVERRIDE_KEY = 'maps.basemap_theme_override';
const PREFS_ENDPOINT = '/api/v1/preferences:bulkSet';
const PREFS_RESET_ENDPOINT = '/api/v1/preferences:reset';
const NAV_FAVORITES_KEY = 'ui.navigation.favorites';
const NAV_RECENTS_KEY = 'ui.navigation.recents';
const NAV_COLLAPSED_KEY = 'ui.navigation.collapsed';
const NAV_FAVORITES_ORDER_KEY = 'ui.navigation.favorites_order';
const NAV_FAVORITES_ORDER_KEY_LEGACY = 'ui.navigation.favoritesOrder';
const PREFERENCES_URL =
  `/api/v1/preferences/effective?keys=${THEME_PREF_KEY},${THEME_EFFECT_KEY},${LAYOUT_PREF_KEY},${TOAST_POSITION_KEY},${BASEMAP_THEME_KEY},${BASEMAP_THEME_OVERRIDE_KEY},${NAV_FAVORITES_KEY},${NAV_RECENTS_KEY},${NAV_COLLAPSED_KEY},${NAV_FAVORITES_ORDER_KEY},${NAV_FAVORITES_ORDER_KEY_LEGACY}`;
const PORTAL_DEBUG_LOGS_ENABLED = import.meta.env.DEV && import.meta.env.MODE !== 'test';

type SavePreferencesPayload = {
  theme?: ThemeOption;
  themeEffect?: ThemeEffect;
  layout?: LayoutOption;
  toastPosition?: ToastPosition;
  basemapTheme?: MapBasemapTheme;
  basemapThemeOverrideEnabled?: boolean;
  navigation?: Partial<NavigationPreferences>;
};

type SavePreferencesOptions = {
  skipRevalidate?: boolean;
  resolvedNavigation?: NavigationPreferences;
};

type EffectivePreferencesResponse = {
  keys?: Record<string, { value?: unknown }>;
};

interface PreferencesContextValue {
  theme: ThemeOption;
  savedTheme: ThemeOption;
  themeEffect: ThemeEffect;
  savedThemeEffect: ThemeEffect;
  isThemePreview: boolean;
  previewTheme: (next: ThemeOption) => void;
  cancelThemePreview: () => void;
  setThemeEffect: (next: ThemeEffect) => void;
  layout: LayoutOption;
  savedLayout: LayoutOption;
  setLayout: (next: LayoutOption) => void;
  toastPosition: ToastPosition;
  savedToastPosition: ToastPosition;
  setToastPosition: (next: ToastPosition) => void;
  basemapTheme: MapBasemapTheme;
  savedBasemapTheme: MapBasemapTheme;
  basemapThemeOverrideEnabled: boolean;
  savedBasemapThemeOverrideEnabled: boolean;
  basemapThemeOverrideSelection: MapBasemapTheme;
  savedBasemapThemeOverrideSelection: MapBasemapTheme;
  setBasemapThemeOverrideEnabled: (next: boolean) => void;
  setBasemapTheme: (next: MapBasemapTheme) => void;
  savePreferences: (updates?: SavePreferencesPayload, options?: SavePreferencesOptions) => Promise<void>;
  resetPreferences: () => Promise<void>;
  refresh: () => Promise<void>;
  loading: boolean;
  error: string | null;
  navigation: NavigationPreferences;
  updateNavigation: (updates: Partial<NavigationPreferences>) => Promise<void>;
}

const PreferencesContext = createContext<PreferencesContextValue | undefined>(undefined);

function logThemeEvent(message: string, details?: Record<string, unknown>) {
  if (!PORTAL_DEBUG_LOGS_ENABLED) {
    return;
  }
  if (details) {
    console.log(`${THEME_LOG_PREFIX} ${message}`, details);
    return;
  }
  console.log(`${THEME_LOG_PREFIX} ${message}`);
}

function applyTheme(theme: ThemeOption) {
  logThemeEvent('applyTheme', { theme });
  applySharedTheme(theme);
}

function applyThemeEffect(effect: ThemeEffect) {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  const body = document.body;
  const target = effect ?? DEFAULT_THEME_EFFECT;

  Array.from(root.classList)
    .filter(className => className.startsWith('theme-effect-'))
    .forEach(className => root.classList.remove(className));
  root.setAttribute('data-theme-effect', target);
  if (target !== 'none') {
    root.classList.add(`theme-effect-${target}`);
  }

  if (body) {
    Array.from(body.classList)
      .filter(className => className.startsWith('theme-effect-'))
      .forEach(className => body.classList.remove(className));
    body.setAttribute('data-theme-effect', target);
    if (target !== 'none') {
      body.classList.add(`theme-effect-${target}`);
    }
  }
}

function persistTheme(theme: ThemeOption) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    logThemeEvent('persistTheme:error', { theme });
  }
}

function persistThemeEffect(effect: ThemeEffect) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(EFFECT_STORAGE_KEY, effect);
  } catch {
    logThemeEvent('persistThemeEffect:error', { effect });
  }
}

function isLayoutOption(value: unknown): value is LayoutOption {
  return value === 'side-menu' || value === 'top-menu';
}

function isToastPosition(value: unknown): value is ToastPosition {
  return value === 'top-left' || value === 'top-right' || value === 'bottom-left' || value === 'bottom-right';
}

function isMapBasemapTheme(value: unknown): value is MapBasemapTheme {
  return (
    value === 'light' ||
    value === 'dark' ||
    value === 'midnight' ||
    value === 'purple-dark' ||
    value === 'bubblegum' ||
    value === 'ocean' ||
    value === 'forest' ||
    value === 'aurora' ||
    value === 'ember' ||
    value === 'citrus' ||
    value === 'sandstone' ||
    value === 'slate' ||
    value === 'glacier' ||
    value === 'cyberpunk' ||
    value === 'rainbow' ||
    value === 'retro' ||
    value === 'noir'
  );
}

function prefersDarkScheme(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function parseBooleanPreference(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'true';
  }
  return fallback;
}

function resolveBasemapTheme(theme: ThemeOption, systemPrefersDark: boolean): MapBasemapTheme {
  if (theme === 'auto') {
    return systemPrefersDark ? 'dark' : 'light';
  }
  return theme;
}

function parseStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return Array.from(
    new Set(
      value
        .filter(item => typeof item === 'string' && item.trim().length > 0)
        .map(item => (item as string).trim())
    )
  );
}

function sanitizeNavigationPreferences(value: NavigationPreferences): NavigationPreferences {
  return {
    favorites: Array.from(new Set(value.favorites.filter(Boolean))),
    recents: Array.from(new Set(value.recents.filter(Boolean))).slice(0, 8),
    collapsed: value.collapsed,
    favoritesOrder: value.favoritesOrder === 'manual' ? 'manual' : 'alpha'
  };
}

function buildNavigationState(
  favorites: string[],
  recents: string[],
  collapsed: boolean,
  favoritesOrder: 'alpha' | 'manual'
): NavigationPreferences {
  return sanitizeNavigationPreferences({
    favorites,
    recents,
    collapsed,
    favoritesOrder
  });
}

function parseCollapsed(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return value === 'true';
  }
  return DEFAULT_NAVIGATION_PREFERENCES.collapsed;
}

function parseFavoritesOrder(value: unknown): 'alpha' | 'manual' {
  return value === 'manual' ? 'manual' : 'alpha';
}

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((value, index) => value === b[index]);
}

function navigationEquals(a: NavigationPreferences, b: NavigationPreferences): boolean {
  if (a.collapsed !== b.collapsed || a.favoritesOrder !== b.favoritesOrder) {
    return false;
  }
  return arraysEqual(a.favorites, b.favorites) && arraysEqual(a.recents, b.recents);
}

async function fetchPreferences(): Promise<EffectivePreferencesResponse> {
  const response = await fetch(PREFERENCES_URL, {
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error('Failed to load preferences');
  }

  const rawText = await response.text();
  if (!rawText.trim()) {
    return {};
  }

  return JSON.parse(rawText) as EffectivePreferencesResponse;
}

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<ThemeOption>(DEFAULT_THEME);
  const [savedTheme, setSavedTheme] = useState<ThemeOption>(DEFAULT_THEME);
  const [themeEffect, setThemeEffectState] = useState<ThemeEffect>(DEFAULT_THEME_EFFECT);
  const [savedThemeEffect, setSavedThemeEffect] = useState<ThemeEffect>(DEFAULT_THEME_EFFECT);
  const [isPreview, setIsPreview] = useState(false);
  const [layout, setLayoutState] = useState<LayoutOption>(DEFAULT_LAYOUT);
  const [savedLayout, setSavedLayout] = useState<LayoutOption>(DEFAULT_LAYOUT);
  const [toastPosition, setToastPositionState] = useState<ToastPosition>(DEFAULT_TOAST_POSITION);
  const [savedToastPosition, setSavedToastPosition] = useState<ToastPosition>(DEFAULT_TOAST_POSITION);
  const [basemapThemeOverrideSelection, setBasemapThemeOverrideSelectionState] =
    useState<MapBasemapTheme>(DEFAULT_BASEMAP_THEME);
  const [savedBasemapThemeOverrideSelection, setSavedBasemapThemeOverrideSelection] =
    useState<MapBasemapTheme>(DEFAULT_BASEMAP_THEME);
  const [basemapThemeOverrideEnabled, setBasemapThemeOverrideEnabledState] = useState(false);
  const [savedBasemapThemeOverrideEnabled, setSavedBasemapThemeOverrideEnabled] = useState(false);
  const [systemPrefersDark, setSystemPrefersDark] = useState(prefersDarkScheme);
  const [navigation, setNavigation] = useState<NavigationPreferences>(DEFAULT_NAVIGATION_PREFERENCES);
  const [savedNavigation, setSavedNavigation] = useState<NavigationPreferences>(DEFAULT_NAVIGATION_PREFERENCES);
  const navigationRef = useRef(navigation);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    navigationRef.current = navigation;
  }, [navigation]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchPreferences();
      const nextTheme = (payload.keys?.[THEME_PREF_KEY]?.value as ThemeOption | undefined) ?? DEFAULT_THEME;
      const nextEffect = isThemeEffect(payload.keys?.[THEME_EFFECT_KEY]?.value)
        ? (payload.keys?.[THEME_EFFECT_KEY]?.value as ThemeEffect)
        : DEFAULT_THEME_EFFECT;
      const nextLayout = isLayoutOption(payload.keys?.[LAYOUT_PREF_KEY]?.value)
        ? (payload.keys?.[LAYOUT_PREF_KEY]?.value as LayoutOption)
        : DEFAULT_LAYOUT;
      const nextToastPosition = isToastPosition(payload.keys?.[TOAST_POSITION_KEY]?.value)
        ? (payload.keys?.[TOAST_POSITION_KEY]?.value as ToastPosition)
        : DEFAULT_TOAST_POSITION;
      const nextBasemapTheme = isMapBasemapTheme(payload.keys?.[BASEMAP_THEME_KEY]?.value)
        ? (payload.keys?.[BASEMAP_THEME_KEY]?.value as MapBasemapTheme)
        : DEFAULT_BASEMAP_THEME;
      const nextBasemapThemeOverrideEnabled = parseBooleanPreference(
        payload.keys?.[BASEMAP_THEME_OVERRIDE_KEY]?.value,
        false
      );
      const nextNavigation = buildNavigationState(
        parseStringArray(payload.keys?.[NAV_FAVORITES_KEY]?.value),
        parseStringArray(payload.keys?.[NAV_RECENTS_KEY]?.value),
        parseCollapsed(payload.keys?.[NAV_COLLAPSED_KEY]?.value),
        parseFavoritesOrder(payload.keys?.[NAV_FAVORITES_ORDER_KEY]?.value ?? payload.keys?.[NAV_FAVORITES_ORDER_KEY_LEGACY]?.value)
      );

      setTheme(nextTheme);
      setSavedTheme(nextTheme);
      applyTheme(nextTheme);
      persistTheme(nextTheme);
      setThemeEffectState(nextEffect);
      setSavedThemeEffect(nextEffect);
      applyThemeEffect(nextEffect);
      persistThemeEffect(nextEffect);
      setLayoutState(nextLayout);
      setSavedLayout(nextLayout);
      setToastPositionState(nextToastPosition);
      setSavedToastPosition(nextToastPosition);
      setBasemapThemeOverrideSelectionState(nextBasemapTheme);
      setSavedBasemapThemeOverrideSelection(nextBasemapTheme);
      setBasemapThemeOverrideEnabledState(nextBasemapThemeOverrideEnabled);
      setSavedBasemapThemeOverrideEnabled(nextBasemapThemeOverrideEnabled);
      setNavigation(nextNavigation);
      setSavedNavigation(nextNavigation);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load preferences';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemPrefersDark(event.matches);
    };
    setSystemPrefersDark(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY && event.newValue) {
        setSavedTheme(event.newValue as ThemeOption);
        if (!isPreview) {
          setTheme(event.newValue as ThemeOption);
          applyTheme(event.newValue as ThemeOption);
        }
      }
      if (event.key === EFFECT_STORAGE_KEY && event.newValue && isThemeEffect(event.newValue)) {
        setSavedThemeEffect(event.newValue);
        setThemeEffectState(event.newValue);
        applyThemeEffect(event.newValue);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [isPreview]);

  const previewTheme = useCallback((next: ThemeOption) => {
    setTheme(next);
    setIsPreview(true);
    applyTheme(next);
  }, []);

  const cancelThemePreview = useCallback(() => {
    setTheme(savedTheme);
    setIsPreview(false);
    applyTheme(savedTheme);
  }, [savedTheme]);

  const setThemeEffect = useCallback((next: ThemeEffect) => {
    setThemeEffectState(next);
    applyThemeEffect(next);
  }, []);

  const setLayout = useCallback((next: LayoutOption) => {
    setLayoutState(next);
  }, []);

  const setToastPosition = useCallback((next: ToastPosition) => {
    setToastPositionState(next);
  }, []);

  const setBasemapTheme = useCallback((next: MapBasemapTheme) => {
    setBasemapThemeOverrideSelectionState(next);
  }, []);

  const setBasemapThemeOverrideEnabled = useCallback((next: boolean) => {
    setBasemapThemeOverrideEnabledState(next);
  }, []);

  const basemapTheme = useMemo(
    () =>
      basemapThemeOverrideEnabled
        ? basemapThemeOverrideSelection
        : resolveBasemapTheme(theme, systemPrefersDark),
    [basemapThemeOverrideEnabled, basemapThemeOverrideSelection, systemPrefersDark, theme]
  );

  const savedBasemapTheme = useMemo(
    () =>
      savedBasemapThemeOverrideEnabled
        ? savedBasemapThemeOverrideSelection
        : resolveBasemapTheme(savedTheme, systemPrefersDark),
    [savedBasemapThemeOverrideEnabled, savedBasemapThemeOverrideSelection, savedTheme, systemPrefersDark]
  );

  const savePreferences = useCallback(
    async (updates: SavePreferencesPayload = {}, options?: SavePreferencesOptions) => {
      const nextTheme = updates.theme ?? theme;
      const nextEffect = updates.themeEffect ?? themeEffect;
      const nextLayout = updates.layout ?? layout;
      const nextToastPosition = updates.toastPosition ?? toastPosition;
      const nextBasemapTheme = updates.basemapTheme ?? basemapThemeOverrideSelection;
      const nextBasemapThemeOverrideEnabled =
        updates.basemapThemeOverrideEnabled ?? basemapThemeOverrideEnabled;
      let nextNavigation = options?.resolvedNavigation ?? navigation;

      if (updates.navigation || options?.resolvedNavigation) {
        nextNavigation =
          options?.resolvedNavigation ??
          sanitizeNavigationPreferences({
            favorites: updates.navigation?.favorites ?? navigation.favorites,
            recents: updates.navigation?.recents ?? navigation.recents,
            collapsed: updates.navigation?.collapsed ?? navigation.collapsed,
            favoritesOrder: updates.navigation?.favoritesOrder ?? navigation.favoritesOrder
          });
      }

      const items: Array<{ key: string; value: unknown; scope: 'USER' }> = [];
      if (nextTheme !== savedTheme) {
        items.push({ key: THEME_PREF_KEY, value: nextTheme, scope: 'USER' });
      }
      if (nextEffect !== savedThemeEffect) {
        items.push({ key: THEME_EFFECT_KEY, value: nextEffect, scope: 'USER' });
      }
      if (nextLayout !== savedLayout) {
        items.push({ key: LAYOUT_PREF_KEY, value: nextLayout, scope: 'USER' });
      }
      if (nextToastPosition !== savedToastPosition) {
        items.push({ key: TOAST_POSITION_KEY, value: nextToastPosition, scope: 'USER' });
      }
      if (nextBasemapTheme !== savedBasemapThemeOverrideSelection) {
        items.push({ key: BASEMAP_THEME_KEY, value: nextBasemapTheme, scope: 'USER' });
      }
      if (nextBasemapThemeOverrideEnabled !== savedBasemapThemeOverrideEnabled) {
        items.push({
          key: BASEMAP_THEME_OVERRIDE_KEY,
          value: nextBasemapThemeOverrideEnabled,
          scope: 'USER'
        });
      }
      if (updates.navigation || options?.resolvedNavigation) {
        if (!arraysEqual(nextNavigation.favorites, savedNavigation.favorites)) {
          items.push({ key: NAV_FAVORITES_KEY, value: nextNavigation.favorites, scope: 'USER' });
        }
        if (!arraysEqual(nextNavigation.recents, savedNavigation.recents)) {
          items.push({ key: NAV_RECENTS_KEY, value: nextNavigation.recents, scope: 'USER' });
        }
        if (nextNavigation.collapsed !== savedNavigation.collapsed) {
          items.push({ key: NAV_COLLAPSED_KEY, value: nextNavigation.collapsed, scope: 'USER' });
        }
        if (nextNavigation.favoritesOrder !== savedNavigation.favoritesOrder) {
          items.push({ key: NAV_FAVORITES_ORDER_KEY, value: nextNavigation.favoritesOrder, scope: 'USER' });
        }
      }

      if (!items.length) {
        setTheme(savedTheme);
        setThemeEffectState(savedThemeEffect);
        setLayoutState(savedLayout);
        setToastPositionState(savedToastPosition);
        setBasemapThemeOverrideSelectionState(savedBasemapThemeOverrideSelection);
        setBasemapThemeOverrideEnabledState(savedBasemapThemeOverrideEnabled);
        setNavigation(savedNavigation);
        setIsPreview(false);
        applyTheme(savedTheme);
        applyThemeEffect(savedThemeEffect);
        return;
      }

      try {
        const response = await fetch(PREFS_ENDPOINT, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          const message = await response.text().catch(() => 'Failed to update preferences');
          throw new Error(message || 'Failed to update preferences');
        }

        setSavedTheme(nextTheme);
        setTheme(nextTheme);
        applyTheme(nextTheme);
        persistTheme(nextTheme);
        setSavedThemeEffect(nextEffect);
        setThemeEffectState(nextEffect);
        applyThemeEffect(nextEffect);
        persistThemeEffect(nextEffect);
        setSavedLayout(nextLayout);
        setLayoutState(nextLayout);
        setSavedToastPosition(nextToastPosition);
        setToastPositionState(nextToastPosition);
        setSavedBasemapThemeOverrideSelection(nextBasemapTheme);
        setBasemapThemeOverrideSelectionState(nextBasemapTheme);
        setSavedBasemapThemeOverrideEnabled(nextBasemapThemeOverrideEnabled);
        setBasemapThemeOverrideEnabledState(nextBasemapThemeOverrideEnabled);
        setIsPreview(false);
        setError(null);

        if ((updates.navigation || options?.resolvedNavigation) && !navigationEquals(nextNavigation, savedNavigation)) {
          setNavigation(nextNavigation);
          setSavedNavigation(nextNavigation);
        }

        if (!options?.skipRevalidate) {
          await load();
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update preferences';
        setError(message);
        throw err;
      }
    },
    [
      layout,
      load,
      navigation,
      savedLayout,
      savedBasemapThemeOverrideEnabled,
      savedBasemapThemeOverrideSelection,
      savedNavigation,
      savedTheme,
      savedThemeEffect,
      savedToastPosition,
      basemapThemeOverrideEnabled,
      basemapThemeOverrideSelection,
      theme,
      themeEffect,
      toastPosition
    ]
  );

  const resetPreferences = useCallback(async () => {
    try {
      const response = await fetch(PREFS_RESET_ENDPOINT, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        const message = await response.text().catch(() => 'Failed to reset preferences');
        throw new Error(message || 'Failed to reset preferences');
      }

      setTheme(DEFAULT_THEME);
      setSavedTheme(DEFAULT_THEME);
      applyTheme(DEFAULT_THEME);
      persistTheme(DEFAULT_THEME);
      setThemeEffectState(DEFAULT_THEME_EFFECT);
      setSavedThemeEffect(DEFAULT_THEME_EFFECT);
      applyThemeEffect(DEFAULT_THEME_EFFECT);
      persistThemeEffect(DEFAULT_THEME_EFFECT);
      setLayoutState(DEFAULT_LAYOUT);
      setSavedLayout(DEFAULT_LAYOUT);
      setToastPositionState(DEFAULT_TOAST_POSITION);
      setSavedToastPosition(DEFAULT_TOAST_POSITION);
      setBasemapThemeOverrideSelectionState(DEFAULT_BASEMAP_THEME);
      setSavedBasemapThemeOverrideSelection(DEFAULT_BASEMAP_THEME);
      setBasemapThemeOverrideEnabledState(false);
      setSavedBasemapThemeOverrideEnabled(false);
      setNavigation(DEFAULT_NAVIGATION_PREFERENCES);
      setSavedNavigation(DEFAULT_NAVIGATION_PREFERENCES);
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset preferences';
      setError(message);
      throw err;
    }
  }, []);

  const updateNavigation = useCallback(
    async (updates: Partial<NavigationPreferences>) => {
      const base = navigationRef.current;
      const optimistic = sanitizeNavigationPreferences({
        favorites: updates.favorites ?? base.favorites,
        recents: updates.recents ?? base.recents,
        collapsed: updates.collapsed ?? base.collapsed,
        favoritesOrder: updates.favoritesOrder ?? base.favoritesOrder
      });
      const previous = base;
      setNavigation(optimistic);
      try {
        await savePreferences({ navigation: updates }, { skipRevalidate: true, resolvedNavigation: optimistic });
      } catch (error) {
        setNavigation(previous);
        throw error;
      }
    },
    [savePreferences]
  );

  const value = useMemo<PreferencesContextValue>(
    () => ({
      theme,
      savedTheme,
      themeEffect,
      savedThemeEffect,
      isThemePreview: isPreview,
      previewTheme,
      cancelThemePreview,
      setThemeEffect,
      layout,
      savedLayout,
      setLayout,
      toastPosition,
      savedToastPosition,
      setToastPosition,
      basemapTheme,
      savedBasemapTheme,
      basemapThemeOverrideEnabled,
      savedBasemapThemeOverrideEnabled,
      basemapThemeOverrideSelection,
      savedBasemapThemeOverrideSelection,
      setBasemapThemeOverrideEnabled,
      setBasemapTheme,
      savePreferences,
      resetPreferences,
      refresh: load,
      loading,
      error,
      navigation,
      updateNavigation
    }),
    [
      cancelThemePreview,
      error,
      isPreview,
      layout,
      load,
      loading,
      navigation,
      previewTheme,
      resetPreferences,
      savePreferences,
      savedLayout,
      savedBasemapTheme,
      savedBasemapThemeOverrideEnabled,
      savedBasemapThemeOverrideSelection,
      savedTheme,
      savedThemeEffect,
      savedToastPosition,
      setBasemapTheme,
      setBasemapThemeOverrideEnabled,
      setLayout,
      setThemeEffect,
      setToastPosition,
      basemapTheme,
      basemapThemeOverrideEnabled,
      basemapThemeOverrideSelection,
      theme,
      themeEffect,
      toastPosition,
      updateNavigation
    ]
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
}
